import { Button } from '../../../../../../widgets/buttons';
import { Icon } from 'src/components/widgets/icon';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { AdvancedSelect } from '../../../../../../widgets/select';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

interface ComposerLinkAttributeNode {
    attributeID: string;
    comparisonOperator: ComposerComparisonOperator;
    optionID: string;
}
interface ComposerLinkOutput {
    logicalOperator: ComposerLogicalOperator;
    links: ComposerLinkAttributeNode[];
}
interface ComposerLinkAttributeDialogProps {
    currentAttribute: ComposerAttributeNode | null;
    modelAttributes: ComposerAttributeNode[];
    currentOption?: ComposerAttributeOptionNode;
    onConfirm: (attributes: ComposerLinkOutput) => void;
}
const ComposerSaveAttributeDialogWindow = styled(Dialog)`
    max-width: 600px;
`;
const Title = styled.h3`
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

const ComposerLinkAttributeDialog: React.FC<ComposerLinkAttributeDialogProps> = ({ onConfirm, currentAttribute, modelAttributes, currentOption }) => {
    const validationContext = useValidationContext();
    const operations = React.useMemo(
        () => [
            { label: T._('All of the following expressions are true', 'Admin'), value: ComposerLogicalOperator.AND },
            { label: T._('At least one of following expressions is true', 'Admin'), value: ComposerLogicalOperator.OR }
        ],
        []
    );

    const constraintOperations = React.useMemo(
        () => [
            { label: '=', value: ComposerComparisonOperator.EQUAL },
            { label: '≠', value: ComposerComparisonOperator.NOT_EQUAL }
        ],
        []
    );
    const [operation, setOperation] = React.useState<ComposerLogicalOperator>(
        currentOption?.linkOperation || currentAttribute?.linkOperation || ComposerLogicalOperator.AND
    );
    const attributesLinks =
        currentOption?.links?.map((l) => {
            return {
                attributeID: l.attributeID,
                comparisonOperator: l.operation,
                optionID: l.optionID
            } as ComposerLinkAttributeNode;
        }) ||
        currentAttribute?.links?.map((l) => {
            return {
                attributeID: l.attributeID,
                comparisonOperator: l.operation,
                optionID: l.optionID
            } as ComposerLinkAttributeNode;
        }) ||
        [];
    const [links, setLinks] = React.useState<ComposerLinkAttributeNode[]>([
        ...(attributesLinks?.length > 0
            ? attributesLinks
            : [
                  {
                      attributeID: '',
                      comparisonOperator: constraintOperations[0].value,
                      optionID: ''
                  }
              ])
    ]);

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const link: ComposerLinkOutput = {
            logicalOperator: operation,
            links: links
        };
        onConfirm(link);
    };

    const shownAttributes = modelAttributes.filter((attr) => attr.type === ComposerAttributeType.Attribute && currentAttribute?.id !== attr.id);

    return (
        <Dialog windowDecorator={ComposerSaveAttributeDialogWindow} buttons={[{ label: T._('Confirm', 'Admin'), onClick: handleConfirm }]}>
            <Title>{T._('Link Attributes', 'Admin')}</Title>
            <Container>
                <FormControl label={T._('Apply if', 'Admin')}>
                    <AdvancedSelect value={operation} onChange={(value) => setOperation(value as ComposerLogicalOperator)}>
                        {operations.map((op) => (
                            <option key={op.value} value={op.value}>
                                {op.label}
                            </option>
                        ))}
                    </AdvancedSelect>
                </FormControl>
                {links.map((attr, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <FormControl label={T._('Attribute', 'Admin')}>
                            <AdvancedSelect
                                value={attr.attributeID}
                                onChange={(value) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].attributeID = value;
                                    newAttributes[index].optionID = shownAttributes.find((a: ComposerAttributeNode) => a.id === value)?.options[0]?.id || '';
                                    setLinks(newAttributes);
                                }}
                                validationKey={`composerLinkAttribute-${index}`}
                                validationLabel={T._('Attribute', 'Admin')}
                                validate={(value) => validateRequiredTrimmed(value?.toString())}
                                validationMessage={T._('Attribute is required.', 'Admin')}
                            >
                                <option value=''>{T._('Select an attribute', 'Admin')}</option>
                                {shownAttributes.map((a: ComposerAttributeNode) => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                            </AdvancedSelect>
                        </FormControl>
                        <FormControl label={T._('Comparison Operator', 'Admin')}>
                            <AdvancedSelect
                                value={attr.comparisonOperator}
                                onChange={(value) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].comparisonOperator = value as ComposerComparisonOperator;
                                    setLinks(newAttributes);
                                }}
                            >
                                {constraintOperations.map((op) => (
                                    <option key={op.value} value={op.value}>
                                        {op.label}
                                    </option>
                                ))}
                            </AdvancedSelect>
                        </FormControl>
                        <FormControl label={T._('Option', 'Admin')}>
                            <AdvancedSelect
                                value={attr.optionID}
                                disabled={!attr.attributeID}
                                onChange={(e) => {
                                    const newAttributes = [...links];
                                    newAttributes[index].optionID = e;
                                    setLinks(newAttributes);
                                }}
                                validationKey={`composerLinkOption-${index}`}
                                validationLabel={T._('Option', 'Admin')}
                                validate={(value) => validateRequiredTrimmed(value?.toString())}
                                validationMessage={T._('Option is required.', 'Admin')}
                            >
                                {shownAttributes
                                    .find((a: ComposerAttributeNode) => a.id === attr.attributeID)
                                    ?.options.map((option: ComposerAttributeOptionNode) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                            </AdvancedSelect>
                        </FormControl>
                        <Button
                            type='button'
                            onClick={() => {
                                const newAttributes = links.filter((_, i) => i !== index);
                                if (newAttributes.length === 0) {
                                    newAttributes.push({
                                        attributeID: '',
                                        comparisonOperator: ComposerComparisonOperator.EQUAL,
                                        optionID: ''
                                    });
                                }
                                setLinks(newAttributes);
                            }}
                            style={{ marginLeft: 8 }}
                        >
                            {T._('Remove', 'Admin')}
                        </Button>
                        {index === links.length - 1 && (
                            <Button
                                type='button'
                                onClick={() => {
                                    setLinks([...links, { attributeID: '', comparisonOperator: ComposerComparisonOperator.EQUAL, optionID: '' }]);
                                }}
                                style={{ marginLeft: 8 }}
                            >
                                <Icon>
                                    <PlusIcon />
                                </Icon>
                                {T._('Add', 'Admin')}
                            </Button>
                        )}
                    </div>
                ))}
            </Container>
        </Dialog>
    );
};

const ComposerLinkAttributeDialogWithValidation: React.FC<ComposerLinkAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerLinkAttributeDialog {...props} />
    </ValidationStateProvider>
);

export default ComposerLinkAttributeDialogWithValidation;
